import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { tv } from 'tailwind-variants';

const shell = tv({
  base: [
    'flex h-full min-h-0 w-full flex-col',
    /* Docked desktop width lock (AC-MD-01). Compact overlays must not force 360px
     * or the close/back control clips off-screen under overflow-hidden parents. */
    'min-w-0 max-w-none',
    /* G7 / EXC-CATALOG-DETAIL-FORM-COLUMN: docked form column (≠ D14 content track). */
    'lg:min-w-[360px] lg:max-w-[420px]',
    'bg-[var(--color-an-surface,white)]',
  ].join(' '),
});

const head = tv({
  base: [
    'flex shrink-0 items-start justify-between gap-3',
    'border-b border-[var(--color-an-border)] px-[var(--space-3)] py-[var(--space-3)] sm:px-[var(--space-4)]',
    /* Stay clear of notches / home indicator when shell is a full-bleed phone sheet. */
    'pt-[max(0.75rem,var(--safe-area-inset-top))]',
  ].join(' '),
});

/** AC-MD-09: body scrolls; header/footer stay pinned. */
const body = tv({
  base: 'admin-scrollbar min-h-0 flex-1 overflow-y-auto px-[var(--space-4)] py-[var(--space-4)]',
});

const foot = tv({
  base: [
    'shrink-0 border-t border-[var(--color-an-border)] px-[var(--space-4)] py-3',
    'pb-[max(0.75rem,var(--safe-area-inset-bottom))]',
  ].join(' '),
});

export interface CatalogDetailPanelShellProps {
  readonly title?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly onClose?: () => void;
  readonly headerActions?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * Docked-only catalog detail panel chrome (P1D-2 / AC-MD-01 width lock).
 * Use inside `CatalogMasterDetailLayout` docked column (`lg+` split).
 * Undocked / compact overlay: pass body-only children to the layout and let
 * `DetailDrawer` own title / back / footer — do not nest this shell as
 * `DetailDrawer` children (avoids double chrome; Wave-1 removed self-chrome).
 * Independent body scroll pairs with the docked layout (AC-MD-09).
 * Compact docked: prominent back control (not a tiny ✕).
 */
export function CatalogDetailPanelShell({
  title,
  subtitle,
  onClose,
  headerActions,
  children,
  footer,
  className,
  testId = 'catalog-detail-panel-shell',
}: CatalogDetailPanelShellProps): JSX.Element {
  const { t } = useTranslation('admin');
  const hasHeader = title != null || subtitle != null || onClose != null || headerActions != null;
  const backLabel = t('common.back', { defaultValue: 'Back' });

  return (
    <div className={shell({ className })} data-testid={testId}>
      {hasHeader ? (
        <header className={head()} data-testid="catalog-detail-panel-header">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {onClose ? (
              <button
                type="button"
                className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-lg px-2 text-sm font-semibold text-[var(--color-an-primary)] hover:bg-[var(--color-an-bg-elevated)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-focus-ring)]"
                aria-label={t('usage.common.i18nHardening.closeDrawerAria')}
                data-testid="catalog-detail-panel-back"
                onClick={onClose}
              >
                <ArrowLeft
                  className="h-[length:var(--icon-size-md)] w-[length:var(--icon-size-md)] shrink-0 stroke-[var(--icon-stroke,1.75)]"
                  aria-hidden
                />
                <span className="max-sm:sr-only">{backLabel}</span>
              </button>
            ) : null}
            <div className="min-w-0 flex-1 pt-1.5">
              {title != null ? (
                <h2 className="truncate text-lg font-semibold text-[var(--color-an-text)]">{title}</h2>
              ) : null}
              {subtitle != null ? (
                <p className="mt-0.5 text-sm text-[var(--color-an-text-muted)]">{subtitle}</p>
              ) : null}
            </div>
          </div>
          {headerActions != null ? (
            <div className="flex shrink-0 items-center gap-1 pt-1.5">{headerActions}</div>
          ) : null}
        </header>
      ) : null}
      <div className={body()} data-testid="catalog-detail-panel-body">
        {children}
      </div>
      {footer != null ? (
        <footer className={foot()} data-testid="catalog-detail-panel-footer">
          {footer}
        </footer>
      ) : null}
    </div>
  );
}
